import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Download, RefreshCw, Building2, CalendarClock, ListChecks, CircleDashed, Crown, Siren, Archive, FileSpreadsheet, LayoutGrid, CalendarDays } from 'lucide-react';
import { downloadCsv } from '../../components/people/peopleUtils';
import { GroupAlertsBanner } from '../../components/groups/GroupAlertsBanner';
import { GroupCalendar } from '../../components/groups/GroupCalendar';
import { StaffGroupsView } from '../../components/groups/StaffGroupsView';
import { PeopleKpiStrip } from '../../components/people/PeopleKpiStrip';
import { FilterChips } from '../../components/people/FilterChips';
import { GroupCard } from '../../components/groups/GroupCard';
import { GroupDetail } from '../../components/groups/GroupDetail';
import { GroupFormModal } from '../../components/groups/GroupFormModal';
import { API, RATING_META, GROUP_TYPES, ratingMeta } from '../../components/groups/groupMeta';

const PortfolioGroupsPage = ({ getAuthHeaders, currentUser }) => {
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState({ staff: [], access: 'manager' });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ open: false, group: null });
  const [q, setQ] = useState('');
  const [kpi, setKpi] = useState(null);
  const [rating, setRating] = useState('all');
  const [type, setType] = useState('all');
  const [mine, setMine] = useState(false);
  const [view, setView] = useState('grid');
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    const h = { headers: getAuthHeaders() };
    try {
      const m = await axios.get(`${API}/meta`, h);
      setMeta(m.data);
      const [g, s] = await Promise.all([axios.get(`${API}/groups`, h), m.data.access === 'staff' ? Promise.resolve({ data: null }) : axios.get(`${API}/summary`, h)]);
      setGroups(g.data); setSummary(s.data); setRefreshKey(k => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load groups'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const exportRegister = () => {
    const rows = groups.map(g => ({ name: g.name, type: g.type, status: g.status, rating: ratingMeta(g.latest_rating).label,
      last_inspected: (g.latest_inspection_at || '').slice(0, 10), next_due: (g.next_inspection_due || '').slice(0, 10),
      leader: g.leader?.name || '', leader_roblox: g.leader?.roblox_username || '', oversight: g.oversight_name || '', people_partner: g.people_partner_name || '', staff_listed: (g.members || []).length, open_actions: g.open_actions }));
    downloadCsv(rows, Object.keys(rows[0] || { name: '' }), `portfolio-register-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} groups`);
  };

  const importPortfolio = async () => {
    try {
      const r = await axios.post(`${API}/groups/import-portfolio`, {}, { headers: getAuthHeaders() });
      toast.success(r.data.count ? `Imported ${r.data.count}: ${r.data.added.join(', ')}` : 'All portfolio groups are already in the register');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  const KPI_FILTER = {
    overdue: g => g.inspection_overdue, actions: g => g.open_actions > 0, never: g => !g.latest_rating && g.status !== 'archived',
    outstanding: g => g.latest_rating === 'outstanding' && g.status !== 'archived',
    urgent: g => ['urgent_improvement', 'needs_improvement'].includes(g.latest_rating) && g.status !== 'archived',
    archived: g => g.status === 'archived',
  };

  const filtered = useMemo(() => groups.filter(g => {
    if (kpi !== 'archived' && g.status === 'archived') return false;
    if (kpi && !KPI_FILTER[kpi](g)) return false;
    if (rating !== 'all' && (g.latest_rating || 'unrated') !== rating) return false;
    if (type !== 'all' && g.type !== type) return false;
    if (mine && ![g.oversight_username, g.people_partner_username].includes(currentUser?.username)) return false;
    if (q && ![g.name, g.leader?.name, g.oversight_name, g.tagline].some(v => v?.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [groups, kpi, rating, type, mine, q]);

  const full = meta.access === 'full';
  const formModal = <GroupFormModal open={form.open} group={form.group} staff={meta.staff} getAuthHeaders={getAuthHeaders}
    onClose={() => setForm({ open: false, group: null })} onSaved={() => { load(); setOpenId(o => o); }} />;

  if (openId) return (
    <>
      <GroupDetail key={`${openId}-${form.open}`} groupId={openId} access={meta.access} staff={meta.staff} currentUsername={currentUser?.username} getAuthHeaders={getAuthHeaders}
        onBack={() => setOpenId(null)} onEdit={(g) => setForm({ open: true, group: g })} onListChanged={load} />
      {formModal}
    </>
  );

  const s = summary || { by_rating: {} };
  const active = groups.filter(g => g.status !== 'archived');
  const ratingOptions = [{ value: 'all', label: 'All', count: active.length }]
    .concat(Object.entries(RATING_META).filter(([k]) => k !== 'shutdown').map(([k, m]) => ({ value: k, label: m.label, hex: m.hex, count: active.filter(g => g.latest_rating === k).length })))
    .concat([{ value: 'unrated', label: 'Not inspected', hex: '#94A3B8', count: active.filter(g => !g.latest_rating).length }]);
  const typeOptions = [{ value: 'all', label: 'All' }].concat(GROUP_TYPES.filter(t => groups.some(g => g.type === t)).map(t => ({ value: t, label: t, count: groups.filter(g => g.type === t).length })));

  return (
    <div className="space-y-6" data-testid="portfolio-groups-page">
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.07] p-6 md:p-8 bg-[#08080f]">
        <div className="pointer-events-none absolute -top-24 -left-10 w-80 h-80 rounded-full bg-cyan-500/15 blur-[110px]" />
        <div className="pointer-events-none absolute -bottom-28 right-0 w-96 h-96 rounded-full bg-violet-600/15 blur-[120px]" />
        <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-2">Starnight Holdings · Portfolio governance</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-white tracking-tight" data-testid="page-title">
              Portfolio <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-300 bg-clip-text text-transparent">Groups</span>
            </h1>
            <p className="text-white/50 text-sm mt-2 max-w-xl">Every group we run, who leads it, who oversees it, and how its latest inspection went.</p>
            {meta.access === 'staff' && <p className="text-[11px] text-white/45 mt-2" data-testid="staff-access-note">Staff view: public group details only. You can raise a concern about any group.</p>}
            {meta.access === 'manager' && <p className="text-[11px] text-amber-300/70 mt-2" data-testid="manager-access-note">Manager access: you can view all groups, and fully work on groups where you're the Oversight or People Partner.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {meta.access !== 'staff' && <button onClick={exportRegister} data-testid="export-register-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs text-white/60 border border-white/[0.1] hover:text-white"><FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV</button>}
            <button onClick={load} data-testid="groups-refresh-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs text-white/60 border border-white/[0.1] hover:text-white"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
            {full && <button onClick={importPortfolio} data-testid="import-portfolio-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs text-white/70 border border-white/[0.1] hover:text-white hover:border-white/25"><Download className="w-3.5 h-3.5" /> Import from website portfolio</button>}
            {full && <button onClick={() => setForm({ open: true, group: null })} data-testid="add-group-btn" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-cyan-400 to-violet-400 shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-transform"><Plus className="w-3.5 h-3.5" /> Add group</button>}
          </div>
        </div>
      </div>

      {meta.access === 'staff' ? (loading ? null : <StaffGroupsView groups={groups} getAuthHeaders={getAuthHeaders} />) : <>
      <GroupAlertsBanner getAuthHeaders={getAuthHeaders} onOpen={setOpenId} refreshKey={refreshKey} />
      <PeopleKpiStrip testIdPrefix="groups-kpi" active={kpi} onSelect={setKpi} items={[
        { id: 'outstanding', label: 'Outstanding', value: s.by_rating.outstanding ?? 0, hex: '#10B981', Icon: Crown, sub: `of ${s.total ?? 0} active groups` },
        { id: 'urgent', label: 'Needs attention', value: (s.by_rating.needs_improvement ?? 0) + (s.by_rating.urgent_improvement ?? 0), hex: '#F43F5E', Icon: Siren, sub: 'Needs / urgent improvement' },
        { id: 'overdue', label: 'Overdue inspections', value: s.overdue ?? 0, hex: '#F59E0B', Icon: CalendarClock },
        { id: 'never', label: 'Never inspected', value: s.never_inspected ?? 0, hex: '#94A3B8', Icon: CircleDashed },
        { id: 'actions', label: 'Open actions', value: s.open_actions ?? 0, hex: '#00E5FF', Icon: ListChecks, sub: `${s.inspections_30d ?? 0} inspections in 30d` },
        { id: 'archived', label: 'Archived', value: s.archived ?? 0, hex: '#64748B', Icon: Archive, sub: 'Shut down groups' },
      ]} />

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4 space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search groups, leaders or oversight…" data-testid="groups-search"
              className="w-full pl-10 pr-4 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white placeholder-white/25 focus:border-cyan-400/40 focus:outline-none text-sm" />
          </div>
          <div className="flex rounded-xl border border-white/[0.07] bg-black/40 p-0.5">
            {[['grid', LayoutGrid], ['calendar', CalendarDays]].map(([m, Ic]) => (
              <button key={m} onClick={() => setView(m)} data-testid={`groups-view-${m}`} className={`px-3 py-1.5 rounded-lg transition-colors ${view === m ? 'bg-cyan-500/20 text-cyan-200' : 'text-white/40 hover:text-white/70'}`}><Ic className="w-4 h-4" /></button>
            ))}
          </div>
          <button onClick={() => setMine(!mine)} data-testid="groups-mine-toggle" data-active={mine}
            className={`px-4 py-2.5 rounded-xl text-xs border transition-colors ${mine ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-100' : 'border-white/[0.07] text-white/55 hover:text-white'}`}>My groups</button>
        </div>
        <FilterChips label="Rating" options={ratingOptions} value={rating} onChange={setRating} testIdPrefix="groups-rating-chip" />
        <FilterChips label="Type" options={typeOptions} value={type} onChange={setType} testIdPrefix="groups-type-chip" />
      </div>

      {view === 'calendar' ? <GroupCalendar key={refreshKey} getAuthHeaders={getAuthHeaders} onOpen={setOpenId} /> : loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-cyan-400 animate-spin" /></div>
        : filtered.length === 0 ? (
          <div className="text-center py-20 rounded-3xl border border-dashed border-white/[0.08]" data-testid="groups-empty">
            <Building2 className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">{groups.length ? 'No groups match these filters' : 'No groups in the register yet'}</p>
            {!groups.length && full && <p className="text-white/35 text-sm mt-1">Import your website portfolio or add a group manually.</p>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="groups-grid">
            {filtered.map((g, i) => <GroupCard key={g.id} group={g} index={i} onOpen={(x) => setOpenId(x.id)} />)}
          </div>
        )}
      </>}
      {formModal}
    </div>
  );
};

export default PortfolioGroupsPage;
